import {
  AppstoreOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined,
  InboxOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  TruckOutlined,
} from '@ant-design/icons';
import {Button, Form, Input, InputNumber, Select, message} from 'antd';
import PropTypes from 'prop-types';
import {useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';

import {RqAdminLookupAllRole, RqGetProducts} from '../../../../reactQuery/apis';
import {RqAddInventoryLot} from '../../../../reactQuery/apis/rqInventoryAPI';
import {selectAuth} from '../../../../redux/selectors';
import {formatInventoryNumber} from '../../../../utils/inventoryUtils';
import {InventoryLotInvoice} from '../InventoryLotInvoice';

const noBlankSpaces = fieldLabel => ({
  validator(_, value) {
    if (
      value !== undefined &&
      value !== null &&
      String(value).trim() === ''
    ) {
      return Promise.reject(
        new Error(`${fieldLabel} cannot be blank spaces`),
      );
    }

    return Promise.resolve();
  },
});

const nonZeroPrice = {
  validator(_, value) {
    if (value !== undefined && Number(value) <= 0) {
      return Promise.reject(
        new Error('Purchase price must be greater than 0'),
      );
    }

    return Promise.resolve();
  },
};

const MAX_QUANTITY = 999999;

const maxQuantity = {
  validator(_, value) {
    if (value !== undefined && Number(value) > MAX_QUANTITY) {
      return Promise.reject(
        new Error(
          `Quantity cannot exceed ${MAX_QUANTITY.toLocaleString()}`,
        ),
      );
    }

    return Promise.resolve();
  },
};

const validHsnCode = {
  validator(_, value) {
    if (!value) {
      return Promise.resolve();
    }

    if (!/^\d+$/.test(String(value).trim())) {
      return Promise.reject(
        new Error('HSN code must contain only numbers'),
      );
    }

    return Promise.resolve();
  },
};

const MAX_GST = 25;

const validGstPercent = label => ({
  validator(_, value) {
    if (value === undefined || value === null) {
      return Promise.resolve();
    }

    const num = Number(value);

    if (num < 0 || num > MAX_GST) {
      return Promise.reject(
        new Error(`${label} must be between 0 and ${MAX_GST}`),
      );
    }

    return Promise.resolve();
  },
});

const AddLotView = ({
  inventoryItem,
  initialData,
  isEditMode,
  onBack,
  onSuccess,
  vendorList = [],
}) => {
  const auth = useSelector(selectAuth);
  const token = auth?.tokenData;
  const [form] = Form.useForm();

  const {data: allUsers = []} = RqAdminLookupAllRole('all', token);

  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [submittedLot, setSubmittedLot] = useState(null);

  /*
   * IMPORTANT:
   * Use the SAME inventory number that already belongs
   * to the selected inventory item.
   *
   * Example:
   * INV-SI-08-09-2026-0008
   */
  const inventoryNumber = useMemo(() => {
    return (
      initialData?.bill?.invoiceNumber ||
      initialData?.lotNumber ||
      inventoryItem?.inventoryNumber ||
      inventoryItem?.inventoryNo ||
      inventoryItem?.inventoryCode ||
      formatInventoryNumber(inventoryItem)
    );
  }, [initialData, inventoryItem]);

  useEffect(() => {
    if (isEditMode && initialData) {
      form.setFieldsValue({
        ...initialData,
        invoiceNumber: inventoryNumber,
      });
    } else if (inventoryNumber) {
      form.setFieldsValue({
        invoiceNumber: inventoryNumber,
      });
    }
  }, [initialData, isEditMode, form, inventoryNumber]);

  const {
    data: productList = [],
    isLoading: productsLoading,
  } = RqGetProducts();

  const {mutate: addLot, isLoading} = RqAddInventoryLot({
    onSuccess: (responseData, variables) => {
      message.success('Lot added successfully!');

      const {payload} = variables;

      const enrichedLot = {
        lotNumber: inventoryNumber,
        supplierName: payload.supplierName,
        warehouse: payload.warehouse,
        purchaseDate: Date.now(),
        manufactureDate: payload.manufactureDate,
        expiryDate: payload.expiryDate,
        bill: payload.bill,
        products: (payload.products || []).map(product => {
          const found = productList.find(
            productItem =>
              (productItem.id || productItem.productId) ===
              product.productId,
          );

          return {
            ...product,
            productName:
              product.productName ||
              found?.productName ||
              found?.name ||
              product.productId ||
              '—',
          };
        }),
      };

      setSubmittedLot(enrichedLot);
      setInvoiceOpen(true);
    },

    onError: err => {
      message.error(err?.message || 'Failed to add lot');
    },
  });

  const recalcGrandTotal = () => {
    const values = form.getFieldsValue(['subtotal']);

    const subtotal = Number(values.subtotal) || 0;

    form.setFieldsValue({
      grandTotal: parseFloat(subtotal.toFixed(2)),
    });
  };

  const handleProductSelect = (productId, rowName) => {
    const selected = productList.find(
      product =>
        product.id === productId ||
        product.productId === productId,
    );

    if (!selected) {
      return;
    }

    const products = form.getFieldValue('products') || [];

    products[rowName] = {
      ...products[rowName],
      productId: selected.id || selected.productId,
      productName: selected.productName || selected.name || '',
      unit: selected.unit || '',
      barcode: selected.barcode || '',
      hsnCode: selected.eanCode || '',
      cgst: selected.tax?.cgst ?? 0,
      sgst: selected.tax?.sgst ?? 0,
    };

    form.setFieldsValue({
      products,
    });
  };

  const validateSubtotalVsQuantity = values => {
    const products = values.products || [];

    const totalQty = products.reduce(
      (sum, product) =>
        sum + (Number(product?.quantity) || 0),
      0,
    );

    const subtotal = Number(values.subtotal) || 0;
    const grandTotal = Number(values.grandTotal) || 0;

    if (totalQty > 0 && subtotal <= 0) {
      message.error(
        'Subtotal cannot be ₹0 when products have a quantity. Please enter the subtotal.',
      );

      return false;
    }

    if (totalQty > 0 && grandTotal <= 0) {
      message.error(
        'Grand Total cannot be ₹0 when products have a quantity. Please recalculate.',
      );

      return false;
    }

    return true;
  };

  const handleSubmit = values => {
    if (!validateSubtotalVsQuantity(values)) {
      return;
    }

    const {
      subtotal,
      grandTotal,
      supplierInvoice,
      products,
      ...rest
    } = values;

    /*
     * Always use the existing inventory number.
     * Never generate a new number here.
     */
    const payload = {
      supplierName: Array.isArray(rest.supplierName)
        ? rest.supplierName[0]?.trim() || ''
        : (rest.supplierName || '').trim(),

      warehouse: (rest.warehouse || '').trim(),

      manufactureDate: rest.manufactureDate
        ? new Date(rest.manufactureDate).getTime()
        : null,

      expiryDate: rest.expiryDate
        ? new Date(rest.expiryDate).getTime()
        : null,

      bill: {
        invoiceNumber: inventoryNumber,
        supplierInvoice,
        subtotal: Number(subtotal) || 0,
        grandTotal: Number(grandTotal) || 0,
      },

      products: (products || []).map(product => ({
        productId: product.productId || '',
        productName: product.productName || '',
        quantity: Number(product.quantity) || 0,
        unit: product.unit || '',
        purchasePrice: Number(product.purchasePrice) || 0,
        sellingPrice: Number(product.sellingPrice) || 0,
        hsnCode: (product.hsnCode || '').trim(),
        barcode: product.barcode || '',
        cgst: Number(product.cgst) || 0,
        sgst: Number(product.sgst) || 0,
      })),
    };

    addLot({
      id: inventoryItem.id,
      payload,
      token,
    });
  };

  const handleInvoiceClose = () => {
    setInvoiceOpen(false);
    setSubmittedLot(null);

    form.resetFields();

    /*
     * After closing the invoice, keep the SAME
     * inventory number.
     */
    form.setFieldsValue({
      invoiceNumber: inventoryNumber,
    });

    onSuccess?.();
  };

  const productRowInitial = {
    productId: '',
    productName: '',
    quantity: 0,
    unit: '',
    purchasePrice: 0,
    sellingPrice: 0,
    hsnCode: '',
    barcode: '',
    cgst: 0,
    sgst: 0,
  };

  const vendorOptions = useMemo(() => {
    return (Array.isArray(allUsers) ? allUsers : [])
      .filter(user => {
        const roles = user?.roles;

        if (Array.isArray(roles)) {
          return roles.some(role =>
            typeof role === 'string'
              ? role.toUpperCase() === 'VENDOR'
              : role?.name?.toUpperCase() === 'VENDOR',
          );
        }

        return (
          typeof roles === 'string' &&
          roles.toUpperCase() === 'VENDOR'
        );
      })
      .map(vendor => {
        const name =
          vendor?.businessName ||
          vendor?.name ||
          vendor?.vendorName ||
          vendor?.companyName ||
          vendor?.username ||
          vendor?.email;

        if (!name) {
          return null;
        }

        return {
          value: name,
          label: name,
        };
      })
      .filter(Boolean);
  }, [allUsers]);

  const productOptions = useMemo(() => {
    return (Array.isArray(productList) ? productList : []).map(product => ({
      value: product.id || product.productId,
      label: product.productName || product.name || 'Unnamed Product',
      category: product.category,
    }));
  }, [productList]);

  return (
    <div>
      <div className={'flex w-full justify-between mb-6'}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={onBack}
          className={'btn-secondary'}
        >
          Back to Inventory
        </Button>

        <div className={'flex items-center justify-between gap-2'}>
          <div
            className={
              'w-8 h-8 rounded-xl flex items-center justify-center text-white'
            }
            style={{
              background:
                'linear-gradient(135deg, #3a5f98, #a7b8d1)',
            }}
          >
            <InboxOutlined style={{fontSize: 16}} />
          </div>

          <div>
            <p
              className={
                'text-base font-semibold text-primary-800 leading-none'
              }
            >
              Add Lot —{' '}
              <span className={'text-blue-600'}>
                {inventoryItem.productName}
              </span>
            </p>

            <p className={'text-xs text-gray-400 mt-0.5'}>
              Current stock: {inventoryItem.totalQuantity}{' '}
              {inventoryItem.unit} ·{' '}
              {inventoryItem.lots?.length || 0} existing lot(s)
            </p>
          </div>
        </div>
      </div>

      <Form
        form={form}
        layout={'vertical'}
        onFinish={handleSubmit}
        requiredMark={false}
      >
        <div
          className={
            'bg-white border border-primary-200 rounded-xl p-6 shadow-sm mb-4'
          }
        >
          <div className={'flex justify-between'}>
            <div className={'flex items-center gap-2 mb-4'}>
              <TruckOutlined className={'text-blue-500'} />

              <span
                className={
                  'font-semibold text-gray-500 uppercase tracking-wide'
                }
              >
                Supplier &amp; Billing Details
              </span>
            </div>

            <div
              className={
                'bg-primary-50 border border-primary-200 rounded-lg px-4 py-3'
              }
            >
              <p
                className={
                  'text-base font-bold text-primary-600 mb-0'
                }
              >
                {inventoryNumber}
              </p>
            </div>
          </div>

          <div
            className={
              'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4'
            }
          >
            <Form.Item
              label={(
                <span
                  className={
                    'text-xs font-semibold text-gray-500 uppercase tracking-wide'
                  }
                >
                  Supplier Name
                </span>
              )}
              name={'supplierName'}
            >
              <Select
                size={'large'}
                placeholder={'Select or enter supplier'}
                showSearch
                allowClear
                mode={'multiple'}
                optionFilterProp={'label'}
                options={vendorOptions}
                notFoundContent={'No vendors found'}
              />
            </Form.Item>

            <Form.Item name={'invoiceNumber'} hidden>
              <Input />
            </Form.Item>

            <Form.Item
              label={(
                <span
                  className={
                    'text-xs font-semibold text-gray-500 uppercase tracking-wide'
                  }
                >
                  Warehouse
                </span>
              )}
              name={'warehouse'}
              rules={[noBlankSpaces('Warehouse')]}
            >
              <Input
                placeholder={'e.g. Warehouse A'}
                size={'large'}
              />
            </Form.Item>

            <Form.Item
              label={(
                <span
                  className={
                    'text-xs font-semibold text-gray-500 uppercase tracking-wide'
                  }
                >
                  Supplier Invoice Number
                </span>
              )}
              name={'supplierInvoice'}
            >
              <Input
                placeholder={'e.g. SUP-INV-12345'}
                size={'large'}
              />
            </Form.Item>

            <Form.Item
              label={(
                <span
                  className={
                    'text-xs font-semibold text-gray-500 uppercase tracking-wide'
                  }
                >
                  Subtotal (₹)
                </span>
              )}
              name={'subtotal'}
              initialValue={0}
            >
              <InputNumber
                min={0}
                step={0.01}
                className={'w-full'}
                size={'large'}
                onChange={recalcGrandTotal}
              />
            </Form.Item>

            <Form.Item
              label={(
                <span
                  className={
                    'text-xs font-semibold text-gray-500 uppercase tracking-wide'
                  }
                >
                  Grand total (₹)
                </span>
              )}
              name={'grandTotal'}
              initialValue={0}
            >
              <InputNumber
                min={0}
                step={0.01}
                className={'w-full'}
                size={'large'}
                style={{borderColor: '#3a5f98'}}
              />
            </Form.Item>
          </div>
        </div>

        <div
          className={
            'bg-white border border-primary-200 rounded-xl p-6 shadow-sm mb-4'
          }
        >
          <div className={'flex items-center gap-2 mb-4'}>
            <AppstoreOutlined className={'text-blue-500'} />

            <span
              className={
                'font-semibold text-gray-500 uppercase tracking-wide'
              }
            >
              Products in this lot
            </span>

            <div className={'flex-1 h-px bg-gray-100'} />
          </div>

          <Form.List
            name={'products'}
            initialValue={[{...productRowInitial}]}
          >
            {(fields, {add, remove}) => (
              <div className={'space-y-4'}>
                {fields.map(({key, name, ...rest}) => (
                  <div
                    key={key}
                    className={
                      'border border-primary-50 rounded-xl p-5 relative'
                    }
                  >
                    <div
                      className={
                        'flex items-center justify-between mb-4'
                      }
                    >
                      <span
                        className={
                          'text-xs font-semibold text-primary-800 uppercase tracking-wide'
                        }
                      >
                        Product #{name + 1}
                      </span>

                      {fields.length > 1 && (
                        <Button
                          danger
                          size={'small'}
                          type={'text'}
                          icon={<MinusCircleOutlined />}
                          onClick={() => remove(name)}
                        >
                          Remove
                        </Button>
                      )}
                    </div>

                    <div
                      className={
                        'mb-4 bg-blue-50 border border-blue-100 rounded-lg p-3'
                      }
                    >
                      <Form.Item
                        {...rest}
                        name={[name, 'productId']}
                        label={(
                          <span
                            className={
                              'text-xs font-semibold text-primary-900 uppercase tracking-wide'
                            }
                          >
                            Select Product Name from Catalog
                          </span>
                        )}
                        className={'mb-0'}
                      >
                        <Select
                          placeholder={'— Search & select product —'}
                          size={'large'}
                          showSearch
                          loading={productsLoading}
                          optionFilterProp={'label'}
                          onChange={id =>
                            handleProductSelect(id, name)
                          }
                          className={'w-full'}
                          options={productOptions.map(product => ({
                            value: product.value,
                            label: product.category
                              ? `${product.label} (${product.category})`
                              : product.label,
                          }))}
                        />
                      </Form.Item>
                    </div>

                    <div
                      className={
                        'grid grid-cols-2 md:grid-cols-6 gap-3'
                      }
                    >
                      <Form.Item
                        {...rest}
                        name={[name, 'quantity']}
                        label={(
                          <span
                            className={
                              'text-xs text-gray-500 uppercase font-semibold'
                            }
                          >
                            Quantity
                          </span>
                        )}
                        rules={[maxQuantity]}
                        initialValue={0}
                      >
                        <InputNumber
                          min={0}
                          max={MAX_QUANTITY}
                          step={0.01}
                          className={'w-full'}
                          size={'middle'}
                        />
                      </Form.Item>

                      <Form.Item
                        {...rest}
                        name={[name, 'unit']}
                        label={(
                          <span
                            className={
                              'text-xs text-gray-500 uppercase font-semibold'
                            }
                          >
                            Unit
                          </span>
                        )}
                      >
                        <Select
                          placeholder={'Unit'}
                          size={'middle'}
                          options={[
                            {
                              value: 'KG',
                              label: 'KG',
                            },
                            {
                              value: 'Ltr',
                              label: 'Ltr',
                            },
                          ]}
                        />
                      </Form.Item>

                      <Form.Item
                        {...rest}
                        name={[name, 'hsnCode']}
                        label={(
                          <span
                            className={
                              'text-xs text-gray-500 uppercase font-semibold'
                            }
                          >
                            HSN Code
                          </span>
                        )}
                        rules={[validHsnCode]}
                      >
                        <Input
                          placeholder={'HSN code'}
                          size={'middle'}
                        />
                      </Form.Item>

                      <Form.Item
                        {...rest}
                        name={[name, 'cgst']}
                        label={(
                          <span
                            className={
                              'text-xs text-gray-500 uppercase font-semibold'
                            }
                          >
                            CGST (%)
                          </span>
                        )}
                        initialValue={0}
                        rules={[validGstPercent('CGST')]}
                      >
                        <InputNumber
                          min={0}
                          max={MAX_GST}
                          step={0.01}
                          className={'w-full'}
                          size={'middle'}
                        />
                      </Form.Item>

                      <Form.Item
                        {...rest}
                        name={[name, 'sgst']}
                        label={(
                          <span
                            className={
                              'text-xs text-gray-500 uppercase font-semibold'
                            }
                          >
                            SGST (%)
                          </span>
                        )}
                        initialValue={0}
                        rules={[validGstPercent('SGST')]}
                      >
                        <InputNumber
                          min={0}
                          max={MAX_GST}
                          step={0.01}
                          className={'w-full'}
                          size={'middle'}
                        />
                      </Form.Item>

                      <Form.Item
                        {...rest}
                        name={[name, 'purchasePrice']}
                        label={(
                          <span
                            className={
                              'text-xs text-gray-500 uppercase font-semibold'
                            }
                          >
                            Purchase Price (₹)
                          </span>
                        )}
                        initialValue={0}
                        rules={[nonZeroPrice]}
                      >
                        <InputNumber
                          min={0}
                          step={0.01}
                          className={'w-full'}
                          size={'middle'}
                        />
                      </Form.Item>
                    </div>
                  </div>
                ))}

                <Button
                  type={'dashed'}
                  onClick={() => add({...productRowInitial})}
                  icon={<PlusOutlined />}
                  className={'w-full'}
                  size={'large'}
                >
                  Add another product
                </Button>
              </div>
            )}
          </Form.List>
        </div>

        <div className={'flex justify-end gap-2'}>
          <Button onClick={onBack}>Cancel</Button>

          <Button
            type={'primary'}
            htmlType={'submit'}
            loading={isLoading}
            icon={<CheckCircleOutlined />}
            size={'large'}
            style={{background: '#3a5f98'}}
          >
            Add Lot
          </Button>
        </div>
      </Form>

      <InventoryLotInvoice
        open={invoiceOpen}
        lot={submittedLot}
        onClose={handleInvoiceClose}
      />
    </div>
  );
};

AddLotView.propTypes = {
  inventoryItem: PropTypes.object.isRequired,
  initialData: PropTypes.object,
  isEditMode: PropTypes.bool,
  onBack: PropTypes.func,
  onSuccess: PropTypes.func,
  vendorList: PropTypes.array,
};

export default AddLotView;
